import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  verifyDocuments,
  getVerificationData,
  getAcceptableDocuments,
  updateVerificationResult,
} from '../../../services/verification';

const isVerificationCompleteMsg = (detail = '') =>
  detail.toLowerCase().includes('verification complete');

/**
 * Kicks off verification and, once the API reports it's complete, fetches
 * the verification data. Call `trigger()` to (re)start; `verificationQuery`
 * is the source of truth for rendered data.
 */
export const useVerification = (listId, { enabled = true } = {}) => {
  const queryClient = useQueryClient();

  const verifyMutation = useMutation({
    mutationFn: () => verifyDocuments(listId),
  });

  const isComplete = isVerificationCompleteMsg(
    verifyMutation.data?.detail ?? verifyMutation.data?.message ?? ''
  );

  const verificationQuery = useQuery({
    queryKey: ['verificationData', listId],
    queryFn: () => getVerificationData(listId),
    enabled: enabled && !!listId && isComplete,
  });

  const trigger = () => verifyMutation.mutate();

  const refetchVerification = () =>
    queryClient.invalidateQueries({ queryKey: ['verificationData', listId] });

  return { verifyMutation, verificationQuery, isComplete, trigger, refetchVerification };
};

export const useAcceptableDocuments = (listId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ['acceptableDocuments', listId],
    queryFn: () => getAcceptableDocuments(listId),
    enabled: enabled && !!listId,
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateVerificationResult = (options = {}) =>
  useMutation({
    mutationFn: (payload) => updateVerificationResult(payload),
    ...options,
  });
